/**
 * Offline support for the website and the iOS app.
 *
 * /sw.js is generated by vite-plugin-pwa. Page requests are NetworkFirst, so
 * the live site always wins when there is a connection and the saved copy is
 * only used when the network fails — the earlier cache-first worker was
 * retired because it could strand people on a stale page.
 *
 * Member pages (journal, dashboard, Learn Hub) are saved too, so they open
 * without a connection. Because that copy is private, every saved page is
 * cleared on sign-out (see clearOfflinePages).
 *
 * Registration never happens in dev, inside an iframe, in Lovable preview, or
 * with ?sw=off — in those contexts any existing worker is removed instead.
 *
 * The push-messaging worker (/sw-push.js) is a separate registration owned by
 * src/lib/push.ts and is deliberately untouched here.
 */

const APP_SW_URL = "/sw.js";

const APP_CACHE_NAMES = ["rt-pages", "rt-assets", "rt-images", "rt-pages-v2", "rt-assets-v2", "rt-images-v2"];

function isBlockedContext(): boolean {
  if (!import.meta.env.PROD) return true;
  // Inside the iOS app shell: never keep saved copies, so every launch shows
  // the latest published site (the app has its own offline page).
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (cap?.isNativePlatform?.()) return true;
  } catch {
    /* ignore */
  }
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const host = window.location.hostname;
  if (host.startsWith("id-preview--") || host.startsWith("preview--")) return true;
  if (host === "lovableproject.com" || host.endsWith(".lovableproject.com")) return true;
  if (host === "lovableproject-dev.com" || host.endsWith(".lovableproject-dev.com"))
    return true;
  if (host === "beta.lovable.dev" || host.endsWith(".beta.lovable.dev")) return true;
  if (new URLSearchParams(window.location.search).get("sw") === "off") return true;
  return false;
}

function isAppCache(name: string): boolean {
  return (
    APP_CACHE_NAMES.includes(name) ||
    /(^|-)precache-v\d+-|(^|-)runtime-|(^|-)googleAnalytics-/.test(name)
  );
}

async function deleteAppCaches() {
  if (typeof caches === "undefined") return;
  const names = await caches.keys();
  await Promise.allSettled(names.filter(isAppCache).map((name) => caches.delete(name)));
}

async function findAppRegistration() {
  const registrations = await navigator.serviceWorker.getRegistrations();
  return registrations.find((r) =>
    (r.active?.scriptURL ?? r.waiting?.scriptURL ?? r.installing?.scriptURL ?? "").endsWith(
      APP_SW_URL,
    ),
  );
}

async function unregisterAppServiceWorker() {
  const existing = await findAppRegistration();
  if (existing) await existing.unregister();
}

/**
 * Removes every saved page and asset. Called on sign-out so a member's private
 * pages are not left readable on a shared device.
 */
export async function clearOfflinePages() {
  if (typeof window === "undefined") return;
  try {
    await deleteAppCaches();
  } catch {
    /* best effort */
  }
}

export function registerAppServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  if (isBlockedContext()) {
    void unregisterAppServiceWorker().catch(() => {});
    void deleteAppCaches().catch(() => {});
    return;
  }

  const run = () => {
    void navigator.serviceWorker.register(APP_SW_URL, { scope: "/" }).catch(() => {
      /* offline support is an enhancement; the site works without it */
    });
  };

  if (document.readyState === "complete") run();
  else window.addEventListener("load", run, { once: true });
}
